import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Search } from "lucide-react";
import { TipEditor, NewTipForm } from "@/components/welcome/TipEditor";

const err = (e) => e?.response?.data?.detail || e?.message || "Request failed";
const STATUS_TONE = { PENDING: "bg-amber-50 text-amber-800", APPROVED: "bg-emerald-50 text-emerald-800", REJECTED: "bg-rose-50 text-rose-800", ARCHIVED: "bg-slate-100 text-slate-600" };
const scopeOf = (t) => t.sku ? `SKU ${t.sku}` : t.product_family ? t.product_family : t.brand && t.category ? `${t.brand} · ${t.category}` : t.brand || t.category || "Generic";

function StatusChips({ counts, status, setStatus }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="tip-status-filter">
      {[["", "All"], ["PENDING", "Pending review"], ["APPROVED", "Approved"], ["REJECTED", "Rejected"], ["ARCHIVED", "Archived"]].map(([k, l]) => (
        <button key={k || "all"} onClick={() => setStatus(k)} data-testid={`tip-filter-${k || "all"}`}
          className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${status === k ? "bg-slate-900 text-white" : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50"}`}>
          {l}{k && counts?.[k] != null ? ` · ${counts[k]}` : ""}
        </button>))}
    </div>
  );
}

function TranslationDots({ t, languages }) {
  const tr = t.translations || {};
  return (
    <div className="flex flex-wrap gap-1">
      {Object.keys(languages).map((l) => {
        const x = tr[l];
        const tone = !x ? "bg-slate-100 text-slate-400" : x.stale ? "bg-rose-50 text-rose-700" : x.approved ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700";
        return <span key={l} title={!x ? "Not translated" : x.stale ? "Out of date" : x.approved ? "Approved" : "Draft, needs approval"} className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${tone}`}>{l}</span>;
      })}
    </div>
  );
}

export function TipLibrary() {
  const [d, setD] = useState(null);
  const [status, setStatus] = useState("PENDING");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [adding, setAdding] = useState(false);
  const load = useCallback(() => api.get("/welcome-label/tips", { params: { status: status || undefined, q: q || undefined } }).then((r) => setD(r.data)).catch((e) => toast.error(err(e))), [status, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  if (!d) return <div className="text-sm text-slate-500">Loading tip library…</div>;
  const current = open && d.tips.find((t) => t.id === open);
  return (
    <div className="space-y-4" data-testid="tip-library">
      <div className="flex flex-wrap items-center gap-3">
        <StatusChips counts={d.counts} status={status} setStatus={setStatus} />
        <div className="relative w-full sm:ml-auto sm:w-64"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tip, SKU, family…" className="pl-8" data-testid="tip-search" /></div>
        <Button variant="create" onClick={() => setAdding((v) => !v)} className="" data-testid="tip-add-btn"><Plus className="mr-1 h-4 w-4" />New tip</Button>
      </div>
      {adding && <NewTipForm families={d.families} onDone={() => { setAdding(false); setStatus("PENDING"); load(); }} />}
      {!d.can_approve && <div className="rounded-md bg-sky-50 px-3 py-2 text-xs text-sky-900" data-testid="tip-staff-note">You can add, edit and draft translations. Management approves before a tip or translation prints.</div>}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        {!d.tips.length && <div className="p-6 text-sm text-slate-500" data-testid="tip-empty">No tips in this view.</div>}
        {d.tips.map((t) => (
          <button key={t.id} onClick={() => setOpen(t.id)} data-testid={`tip-row-${t.id}`}
            className="grid w-full grid-cols-1 gap-2 border-b border-slate-100 px-4 py-3 text-left transition-colors last:border-0 hover:bg-slate-50 md:grid-cols-[1fr_150px_150px_110px]">
            <div className="min-w-0"><div className="text-sm text-slate-900">{t.text}</div><div className="mt-1 text-xs text-slate-500">{t.family.replace(/_/g, " ")} · {scopeOf(t)} · {t.source === "MANUAL" ? "Added by team" : "Suntronix library"}</div></div>
            <TranslationDots t={t} languages={d.languages} />
            <div className="text-xs text-slate-600" data-testid={`tip-usage-${t.id}`}>{t.labels} labels · {t.scans} scans</div>
            <span className={`h-fit w-fit rounded-full px-2 py-0.5 text-[11px] font-bold ${STATUS_TONE[t.status]}`} data-testid={`tip-status-${t.id}`}>{t.status}</span>
          </button>))}
      </div>
      {current && <TipEditor tip={current} families={d.families} languages={d.languages} canApprove={d.can_approve} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
}
